/**
 * Global URL configuration for room booking management
 * Handles dynamic API URLs from data attributes in DOM elements
 */

class UrlConfig {
    constructor() {
        this.urls = {
            userApiUrl: null,
            roomBookingApiUrl: null,
            adminApiUrl: null,
            accountApiUrl: null,
            helpUrl: null,
        };
		this.initialized = false;
	}

	/**
	 * Initialize URL configuration from data attributes
	 * This should be called once during application setup
	 * @param {Object} config - Configuration object with URL properties
	 * @param {string} config.userApiUrl - Base URL for user API endpoints
	 * @param {string} config.roomBookingApiUrl - Base URL for room booking API endpoints
	 * @param {string} [config.adminApiUrl] - Base URL for admin API endpoints (optional)
	 * @param {string} [config.helpUrl] - Base URL for help/FAQ website (optional)
	 */
    init(config) {
		if (this.initialized) {
			console.warn('UrlConfig already initialized');
			return;
		}

        this.urls.userApiUrl = config.userApiUrl;
        this.urls.roomBookingApiUrl = config.roomBookingApiUrl;
        if (config.adminApiUrl) {
            this.urls.adminApiUrl = config.adminApiUrl;
        }
        if (config.accountApiUrl) {
            this.urls.accountApiUrl = config.accountApiUrl;
        }
		if (config.helpUrl) {
			this.urls.helpUrl = config.helpUrl;
		}

		this.initialized = true;
	}

	/**
	 * Get user API base URL
	 * @returns {string} User API base URL
	 */
	getUserApiUrl() {
		if (!this.initialized) {
			throw new Error('UrlConfig not initialized. Call init() first.');
		}
		return this.urls.userApiUrl;
	}

	/**
	 * Get room booking API base URL
	 * @returns {string} Room booking API base URL
	 */
	getRoomBookingApiUrl() {
		if (!this.initialized) {
			throw new Error('UrlConfig not initialized. Call init() first.');
		}
		return this.urls.roomBookingApiUrl;
	}

	/**
	 * Get admin API base URL
	 * @returns {string} Admin API base URL
	 */
    getAdminApiUrl() {
		if (!this.initialized) {
			throw new Error('UrlConfig not initialized. Call init() first.');
		}
        if (!this.urls.adminApiUrl) {
            throw new Error('Admin API URL not configured');
        }
        return this.urls.adminApiUrl;
    }

    /**
     * Get account API base URL
     * @returns {string} Account API base URL
     */
    getAccountApiUrl() {
        if (!this.initialized) {
            throw new Error('UrlConfig not initialized. Call init() first.');
        }
        if (!this.urls.accountApiUrl) {
            throw new Error('Account API URL not configured');
        }
        return this.urls.accountApiUrl;
    }

	/**
	 * Check if URLs are initialized
	 * @returns {boolean} True if initialized
	 */
	isInitialized() {
		return this.initialized;
	}

	/**
	 * Build full endpoint URL
	 * @param {'user'|'roomBooking'|'admin'} apiType - API type
	 * @param {string} endpoint - Endpoint path (e.g., '/fetch-bookings-for-room')
	 * @returns {string} Full endpoint URL
	 */
	buildUrl(apiType, endpoint) {
		let baseUrl;
        switch (apiType) {
            case 'user':
                baseUrl = this.getUserApiUrl();
                break;
            case 'roomBooking':
                baseUrl = this.getRoomBookingApiUrl();
                break;
            case 'admin':
                baseUrl = this.getAdminApiUrl();
                break;
            case 'account':
                baseUrl = this.getAccountApiUrl();
                break;
            default:
                throw new Error(`Unknown API type: ${apiType}`);
        }

		// Ensure endpoint starts with /
		const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
		
		// Remove trailing slash from baseUrl and combine
		return `${baseUrl.replace(/\/$/, '')}${normalizedEndpoint}`;
	}
}

// Export singleton instance
export const urlConfig = new UrlConfig();

// Export convenience functions
export const initUrlConfig = (config) => urlConfig.init(config);
export const getUserApiUrl = () => urlConfig.getUserApiUrl();
export const getRoomBookingApiUrl = () => urlConfig.getRoomBookingApiUrl();
export const getAdminApiUrl = () => urlConfig.getAdminApiUrl();
export const getAccountApiUrl = () => urlConfig.getAccountApiUrl();
export const buildApiUrl = (apiType, endpoint) => urlConfig.buildUrl(apiType, endpoint);
